import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Textarea,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesCompanyApi, type HedgeInvoice, type HedgeQuote } from "@/services/api";
import {
  Shield,
  ArrowLeft,
  Upload,
  FileText,
  Check,
  Loader2,
  AlertCircle,
  DollarSign,
  Calendar,
  RefreshCw,
  Info,
  CheckCircle2,
  Circle,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Steps do fluxo (simplificado - sem etapa de garantia aqui)
type Step = "upload" | "dados" | "confirmar";

const steps: { key: Step; label: string; description: string }[] = [
  { key: "upload", label: "Upload", description: "Envie a invoice" },
  { key: "dados", label: "Dados", description: "Confirme os dados" },
  { key: "confirmar", label: "Confirmar", description: "Revise a cotação" },
];

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatBrl(value: string | number | undefined): string {
  if (!value) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDateBr(dateString: string): string {
  if (!dateString) return "";
  // Se já está no formato ISO (YYYY-MM-DD), converte para DD/MM/YYYY
  if (dateString.includes("-")) {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  }
  return dateString;
}

interface InvoiceFormData {
  invoiceNumber: string;
  supplierName: string;
  supplierCountry: string;
  amountUsdc: number;
  dueDate: string;
  description: string;
}

export function NovoHedgePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>("upload");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dados do fluxo
  const [parseResult, setParseResult] = useState<{
    data?: InvoiceFormData;
    confidence: number;
  } | null>(null);
  const [formData, setFormData] = useState<InvoiceFormData>({
    invoiceNumber: "",
    supplierName: "",
    supplierCountry: "",
    amountUsdc: 0,
    dueDate: "",
    description: "",
  });
  const [invoice, setInvoice] = useState<HedgeInvoice | null>(null);
  const [quote, setQuote] = useState<HedgeQuote | null>(null);
  const [quoteExpired, setQuoteExpired] = useState(false);
  const [quoteCountdown, setQuoteCountdown] = useState(30);
  const [hedgeAmountUsdc, setHedgeAmountUsdc] = useState<number>(0);
  const [amountInputValue, setAmountInputValue] = useState<string>("");
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Step 1: Upload e parse
  const handleFileUpload = useCallback(async (selectedFile: File) => {
    setError(null);
    setLoading(true);

    try {
      // Converter arquivo para base64
      const reader = new FileReader();
      const fileContent = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      // Enviar para parsing
      const result = await hedgesCompanyApi.parseInvoice({
        fileContent,
        fileName: selectedFile.name,
        mimeType: selectedFile.type,
      });

      if (result.success && result.data) {
        // Check if the currency is not USD
        if (result.error && result.data.currency) {
          setError(result.error);
        }

        setParseResult({
          data: {
            invoiceNumber: result.data.invoiceNumber || "",
            supplierName: result.data.supplierName || "",
            supplierCountry: result.data.supplierCountry || "",
            amountUsdc: result.data.amountUsdc || 0,
            dueDate: result.data.dueDate || "",
            description: result.data.description || "",
          },
          confidence: result.confidence,
        });
        setFormData({
          invoiceNumber: result.data.invoiceNumber || "",
          supplierName: result.data.supplierName || "",
          supplierCountry: result.data.supplierCountry || "",
          amountUsdc: result.data.amountUsdc || 0,
          dueDate: result.data.dueDate || "",
          description: result.data.description || "",
        });
        setHedgeAmountUsdc(result.data.amountUsdc || 0);
        setAmountInputValue(result.data.amountUsdc ? result.data.amountUsdc.toString() : "");
        setCurrentStep("dados");
      } else {
        // Se não conseguiu extrair, permite entrada manual
        if (result.error) {
          setError(result.error);
        }
        setParseResult({ confidence: 0 });
        setCurrentStep("dados");
      }
    } catch (err) {
      console.error("Erro ao processar arquivo:", err);
      setError("Erro ao processar o arquivo. Tente novamente ou insira os dados manualmente.");
      // Permite continuar manualmente
      setParseResult({ confidence: 0 });
      setCurrentStep("dados");
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2: Confirmar dados e criar invoice
  const handleConfirmData = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      // Criar invoice
      const createdInvoice = await hedgesCompanyApi.createInvoice({
        ...formData,
        extractionConfidence: parseResult?.confidence,
      });

      // Confirmar invoice
      const confirmedInvoice = await hedgesCompanyApi.confirmInvoice(createdInvoice.id);
      setInvoice(confirmedInvoice);

      // Obter cotação
      await handleRequestQuote(confirmedInvoice.id);
    } catch (err: unknown) {
      console.error("Erro ao criar invoice:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao criar invoice";
      setError(errorMessage);
      setLoading(false);
    }
  }, [formData, parseResult, hedgeAmountUsdc]);

  // Solicitar cotação
  const handleRequestQuote = useCallback(async (invoiceId?: string, isAutoRefresh = false) => {
    setError(null);
    if (!isAutoRefresh) {
      setLoading(true);
    }
    setIsRefreshingQuote(isAutoRefresh);
    setQuoteExpired(false);

    // Limpar intervalo anterior se existir
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    try {
      const id = invoiceId || invoice?.id;
      if (!id) throw new Error("Invoice não encontrada");

      const newQuote = await hedgesCompanyApi.requestQuote({
        invoiceId: id,
        hedgeAmountUsdc: hedgeAmountUsdc || formData.amountUsdc,
      });

      setQuote(newQuote);
      setCurrentStep("confirmar");

      // Iniciar countdown baseado na expiração real do provedor
      const initialSeconds = Math.max(0, Math.floor(
        (new Date(newQuote.validUntil).getTime() - Date.now()) / 1000
      ));
      setQuoteCountdown(initialSeconds);
      countdownIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.floor(
          (new Date(newQuote.validUntil).getTime() - Date.now()) / 1000
        ));
        setQuoteCountdown(remaining);
        if (remaining <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setQuoteExpired(true);
        }
      }, 1000);
    } catch (err: unknown) {
      console.error("Erro ao obter cotação:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao obter cotação";
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsRefreshingQuote(false);
    }
  }, [invoice, hedgeAmountUsdc, formData.amountUsdc]);

  // Auto-refresh quando a cotação expira
  useEffect(() => {
    if (quoteExpired && currentStep === "confirmar" && invoice?.id && !loading && !isRefreshingQuote) {
      handleRequestQuote(invoice.id, true);
    }
  }, [quoteExpired, currentStep, invoice?.id, loading, isRefreshingQuote, handleRequestQuote]);

  // Criar hedge (rascunho - sem termo)
  const handleCreateHedge = useCallback(async () => {
    if (!invoice || !quote) return;

    setLoading(true);
    setError(null);

    try {
      // Criar hedge (rascunho - termo será aceito na página de garantia)
      const hedge = await hedgesCompanyApi.createHedge({
        invoiceId: invoice.id,
        quoteId: quote.id,
      });

      // Redirecionar para página de garantia
      navigate(`/protecao-cambial/${hedge.id}/garantia`);
    } catch (err: unknown) {
      console.error("Erro ao criar hedge:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao criar hedge";

      if (errorMessage.includes("expirada") || errorMessage.includes("expired")) {
        setQuoteExpired(true);
        setError("Cotação expirada. Aguarde a atualização automática.");
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [invoice, quote, navigate]);

  // Cleanup do intervalo ao desmontar
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Pular upload (entrada manual)
  const handleSkipUpload = () => {
    setError(null);
    setParseResult({ confidence: 0 });
    setCurrentStep("dados");
  };

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Nova Proteção Cambial"
        description="Crie uma proteção cambial para sua importação"
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => navigate("/protecao-cambial")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      />

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;

          return (
            <div key={step.key} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors",
                    isCompleted && "bg-green-500 border-green-500 text-white",
                    isCurrent && "bg-primary border-primary text-white",
                    !isCompleted && !isCurrent && "bg-muted border-muted-foreground/30 text-muted-foreground"
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs mt-2 font-medium",
                    isCurrent ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "w-6 sm:w-10 md:w-16 h-0.5 mx-1 sm:mx-2",
                    index < currentStepIndex ? "bg-green-500" : "bg-muted-foreground/30"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Upload */}
      {currentStep === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload da Invoice
            </CardTitle>
            <CardDescription>
              Faça upload da invoice de importação (PDF ou XML) para extrair os dados automaticamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dropzone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
                "hover:border-primary hover:bg-primary/5",
                loading && "opacity-50 pointer-events-none"
              )}
              onClick={() => document.getElementById("file-input")?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const droppedFile = e.dataTransfer.files[0];
                if (droppedFile) handleFileUpload(droppedFile);
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.xml"
                className="hidden"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0];
                  if (selectedFile) handleFileUpload(selectedFile);
                }}
              />

              {loading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                  <div>
                    <p className="font-medium">Analisando invoice...</p>
                    <p className="text-sm text-muted-foreground">
                      Nossa IA está extraindo os dados
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="font-medium mb-1">
                    Arraste sua invoice aqui ou clique para selecionar
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Formatos aceitos: PDF, XML (até 10MB)
                  </p>
                </>
              )}
            </div>

            {/* Dica */}
            <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Nossa IA irá extrair automaticamente:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>Número da invoice</li>
                  <li>Nome do fornecedor</li>
                  <li>Valor em USDC</li>
                  <li>Data de vencimento</li>
                </ul>
              </div>
            </div>

            {/* Botão para entrada manual */}
            <div className="text-center">
              <Button variant="ghost" onClick={handleSkipUpload}>
                Ou insira os dados manualmente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Confirmar Dados */}
      {currentStep === "dados" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Dados da Invoice
            </CardTitle>
            <CardDescription>
              {parseResult?.confidence && parseResult.confidence > 0.5 ? (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Dados extraídos automaticamente (confiança: {Math.round(parseResult.confidence * 100)}%)
                  - verifique e ajuste se necessário
                </span>
              ) : (
                "Preencha os dados da invoice de importação"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Número da Invoice *</Label>
                <Input
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, invoiceNumber: e.target.value })
                  }
                  placeholder="INV-2024-001"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplierName">Fornecedor *</Label>
                <Input
                  id="supplierName"
                  value={formData.supplierName}
                  onChange={(e) =>
                    setFormData({ ...formData, supplierName: e.target.value })
                  }
                  placeholder="Nome do fornecedor"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplierCountry">País de Origem</Label>
                <Input
                  id="supplierCountry"
                  value={formData.supplierCountry}
                  onChange={(e) =>
                    setFormData({ ...formData, supplierCountry: e.target.value })
                  }
                  placeholder="Estados Unidos"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amountUsdc">Valor em USDC *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="amountUsdc"
                    type="text"
                    inputMode="decimal"
                    value={
                      isAmountFocused
                        ? amountInputValue
                        : formData.amountUsdc
                          ? formData.amountUsdc.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : ""
                    }
                    onChange={(e) => {
                      // Permite apenas números, ponto e vírgula durante digitação
                      const rawValue = e.target.value.replace(/[^\d.,]/g, "");
                      setAmountInputValue(rawValue);
                      // Converte para número removendo formatação
                      const normalizedValue = rawValue.replace(/,/g, "");
                      const value = parseFloat(normalizedValue) || 0;
                      setFormData({ ...formData, amountUsdc: value });
                      setHedgeAmountUsdc(value);
                    }}
                    onFocus={() => {
                      setIsAmountFocused(true);
                      // Mostra valor sem formatação ao focar
                      setAmountInputValue(formData.amountUsdc ? formData.amountUsdc.toString() : "");
                    }}
                    onBlur={() => {
                      setIsAmountFocused(false);
                    }}
                    placeholder="50,000.00"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Data de Vencimento *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, dueDate: e.target.value })
                    }
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descrição dos produtos/serviços"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setError(null);
                  setCurrentStep("upload");
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <Button
                onClick={handleConfirmData}
                disabled={
                  loading ||
                  !formData.invoiceNumber ||
                  !formData.supplierName ||
                  !formData.amountUsdc ||
                  !formData.dueDate
                }
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Continuar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirmar Cotação */}
      {currentStep === "confirmar" && quote && (
        <div className="space-y-6">
          {/* Card do Valor Garantido */}
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Valor Final Garantido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <p className="text-3xl font-bold text-green-700">
                  {formatBrl(quote.lockedAmountBrl)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Baseado em uma taxa de referência de R$ {parseFloat(quote.spotRate).toFixed(4)}
                </p>
              </div>

              <div className="mt-4 pt-4 border-t space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Fee de proteção:</span>
                  <span className="font-semibold">{formatBrl(quote.protectionFeeBrl || quote.spreadAmountBrl)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card dos Valores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="h-4 w-4" />
                  Valores do Hedge
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Valor final garantido em BRL</Label>
                  <p className="text-2xl font-bold text-primary">
                    {formatBrl(quote.lockedAmountBrl)}
                  </p>
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">Valor do hedge</Label>
                  <p className="text-lg font-medium">{formatUsdc(hedgeAmountUsdc)}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-4 w-4" />
                  Garantia e Vencimento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">
                    Garantia exigida ({quote.guaranteePercentage}%)
                  </Label>
                  <p className="text-xl font-bold">{formatUsdc(quote.guaranteeAmountUsdc)}</p>
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">Vencimento</Label>
                  <p className="text-xl font-bold">{formatDateBr(formData.dueDate)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info sobre próxima etapa */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Próxima etapa: Depositar Garantia</p>
                  <p className="mt-1 text-blue-700">
                    Ao clicar em "Criar Proteção", você será direcionado para a página de depósito de garantia,
                    onde poderá renovar a cotação com valores atualizados e aceitar o Termo de Proteção Cambial.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cotação válida / expirada / atualizando */}
          {isRefreshingQuote ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span>Atualizando cotação...</span>
            </div>
          ) : quoteExpired ? (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Cotação expirada - atualizando...</span>
                  </div>
                  <RefreshCw className="h-4 w-4 animate-spin text-orange-600" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Circle className="h-2 w-2 fill-green-500 text-green-500" />
              Cotação válida por: <span className="font-mono tabular-nums font-medium">{quoteCountdown}s</span>
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => setCurrentStep("dados")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleCreateHedge}
              disabled={loading || quoteExpired || isRefreshingQuote}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              Criar Proteção
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
